import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Form } from "../../../components/Form";
import { Input } from "../../../components/Input";
import { MoneyInput } from "../../../components/MoneyInput";
import { Button, IconButton } from "../../../components/Button";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type { OfferCombo, OfferComboSlot } from "@KassaroAB/shared/types/offer";
import {
  asDecimal,
  createMoney,
} from "@KassaroAB/shared/utils/money";
import { OfferCommonFields } from "./OfferCommonFields";
import { OfferTargetFields } from "./OfferTargetFields";
import type { OfferTypeFormProps } from "./offerFormTypes";
import { Fields, SlotCard } from "./offerFormStyles";
import {
  buildOfferBase,
  emptyOfferTarget,
  getOfferCommonDefaults,
} from "./offerUtils";
import { useOfferFormCatalog } from "./useOfferFormCatalog";
import { useOfferFormLifecycle } from "./useOfferFormLifecycle";

export const ComboForm = ({
  formId,
  offer,
  savedTick,
  onSubmit,
  onDirtyChange,
  onTypeChange,
}: OfferTypeFormProps) => {
  const { t } = useTranslation();
  const { currency, productOptions, groupOptions } = useOfferFormCatalog();
  const comboOffer =
    offer?.type === OfferType.COMBO ? (offer as OfferCombo) : null;
  const isEditing = !!offer;
  const initialSlots: OfferComboSlot[] =
    comboOffer?.slots ?? [{ target: emptyOfferTarget(), quantity: 1 }];

  const form = useForm({
    defaultValues: {
      ...getOfferCommonDefaults(
        comboOffer ??
          (offer ? { ...offer, type: OfferType.COMBO } : undefined),
      ),
      type: OfferType.COMBO as OfferType,
      priceMajor: comboOffer ? asDecimal(comboOffer.price) : 0,
      takeawayPriceMajor: comboOffer?.takeawayPrice
        ? asDecimal(comboOffer.takeawayPrice)
        : 0,
      slots: initialSlots,
    },
    onSubmit: ({ value }) => {
      const takeawayMinor = Math.round(Number(value.takeawayPriceMajor) * 100);
      onSubmit({
        ...buildOfferBase(value),
        type: OfferType.COMBO,
        slots: value.slots.map((slot) => ({
          target: slot.target,
          quantity: Math.max(1, Number(slot.quantity)),
        })),
        price: createMoney(
          Math.round(Number(value.priceMajor) * 100),
          currency,
        ),
        takeawayPrice:
          takeawayMinor > 0 ? createMoney(takeawayMinor, currency) : null,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  useOfferFormLifecycle({ form, savedTick, onDirtyChange, isDirty });

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <OfferCommonFields
        form={form}
        isEditing={isEditing}
        onTypeChange={onTypeChange}
      />
      <FormSection title={t("offerForm.additionalSettings")}>
        <Fields>
          <form.Field name="priceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.price")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
                required
              />
            )}
          </form.Field>
          <form.Field name="takeawayPriceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.takeawayPrice")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>

      <form.Field name="slots">
        {(field) => (
          <FormSection title={t("offerForm.slots")}>
            <Fields>
              {field.state.value.map((slot, index) => (
                <SlotCard key={index}>
                  <FormLabel>
                    {t("offerForm.slot", { number: index + 1 })}
                  </FormLabel>
                  <OfferTargetFields
                    value={slot.target}
                    onChange={(nextTarget) => {
                      field.handleChange((current) => {
                        const next = [...current];
                        next[index] = { ...next[index], target: nextTarget };
                        return next;
                      });
                    }}
                    products={productOptions}
                    groups={groupOptions}
                    productsLabel={t("offerForm.products")}
                    groupsLabel={t("offerForm.productGroups")}
                  />
                  <FormGroup>
                    <FormLabel>{t("offerForm.quantity")}</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      max={99}
                      value={slot.quantity}
                      onChange={(e) => {
                        const quantity = Math.max(
                          1,
                          Number(e.target.value) || 1,
                        );
                        field.handleChange((current) => {
                          const next = [...current];
                          next[index] = { ...next[index], quantity };
                          return next;
                        });
                      }}
                    />
                  </FormGroup>
                  {field.state.value.length > 1 ? (
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("offerForm.removeSlot")}
                      onClick={() => {
                        field.handleChange(
                          field.state.value.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  ) : null}
                </SlotCard>
              ))}
              <Button
                type="button"
                $variant="outlined"
                $small
                onClick={() => {
                  field.handleChange([
                    ...field.state.value,
                    { target: emptyOfferTarget(), quantity: 1 },
                  ]);
                }}
              >
                <Plus size={16} />
                {t("offerForm.addSlot")}
              </Button>
            </Fields>
          </FormSection>
        )}
      </form.Field>
    </Form>
  );
};
